import { useState } from 'react';
import axios from 'axios';
import {
  Card, Input, Modal, Form, Typography, Space,
  Checkbox, Alert, Row, Col, App as AntApp, Flex, Collapse,
} from 'antd';
import { API_URL } from '../../config';
import type { Usuario, Grupo } from './types';
import { getToken } from './utils';
import FotoUploader from './FotoUploader';

const { Text } = Typography;

// Modal "Editar usuário" (nome, cargo, foto, CREA, perfis).
// Bloqueia remoção do perfil `admin` se for o próprio usuário (anti-lockout).
export default function FormGrupos({ usuario, grupos, eEuMesmo, onClose, onSaved }: {
  usuario: Usuario; grupos: Grupo[]; eEuMesmo: boolean;
  onClose: () => void; onSaved: () => void;
}) {
  const { message } = AntApp.useApp();
  const [selecionados, setSelecionados] = useState<string[]>([...usuario.groups]);
  const [nome, setNome] = useState(usuario.nome || '');
  const [cargo, setCargo] = useState((usuario as any).cargo || '');
  const [fotoUrl, setFotoUrl] = useState((usuario as any).foto_url || '');
  const [telComercial, setTelComercial] = useState((usuario as any).telefone_comercial || '');
  const [crea, setCrea] = useState((usuario as any).crea || '');
  const [creaRnp, setCreaRnp] = useState((usuario as any).crea_rnp || '');
  const [creaTitulo, setCreaTitulo] = useState((usuario as any).crea_titulo || '');
  const [linkedin, setLinkedin] = useState((usuario as any).linkedin || '');
  const [salvando, setSalvando] = useState(false);

  function toggle(g: string) {
    if (eEuMesmo && g === 'admin' && selecionados.includes('admin')) {
      message.warning('Anti-lockout: peça a outro administrador.');
      return;
    }
    setSelecionados(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  }

  async function salvar() {
    if (!nome.trim()) { message.warning('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      const attrPayload: any = {};
      if (nome.trim() !== (usuario.nome || '').trim()) attrPayload.nome = nome.trim();
      if (cargo !== ((usuario as any).cargo || '')) attrPayload.cargo = cargo;
      if (fotoUrl !== ((usuario as any).foto_url || '')) {
        if (fotoUrl.startsWith('data:')) attrPayload.foto_base64 = fotoUrl;
        else attrPayload.foto_url = fotoUrl;
      }
      if (telComercial !== ((usuario as any).telefone_comercial || '')) attrPayload.telefone_comercial = telComercial;
      if (crea !== ((usuario as any).crea || '')) attrPayload.crea = crea;
      if (creaRnp !== ((usuario as any).crea_rnp || '')) attrPayload.crea_rnp = creaRnp;
      if (creaTitulo !== ((usuario as any).crea_titulo || '')) attrPayload.crea_titulo = creaTitulo;
      if (linkedin !== ((usuario as any).linkedin || '')) attrPayload.linkedin = linkedin;
      if (Object.keys(attrPayload).length > 0) {
        await axios.patch(`${API_URL}/admin/usuarios/${encodeURIComponent(usuario.email)}`,
          attrPayload, { headers });
      }
      const grupoChanged = JSON.stringify([...selecionados].sort()) !== JSON.stringify([...usuario.groups].sort());
      if (grupoChanged) {
        await axios.patch(`${API_URL}/admin/usuarios/${encodeURIComponent(usuario.email)}/groups`,
          { groups: selecionados }, { headers });
      }
      message.success('Salvo.');
      onSaved(); onClose();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} onOk={salvar} confirmLoading={salvando} width={580} destroyOnClose
      title={
        <Space direction="vertical" size={2}>
          <Text strong style={{ color: '#020D7F' }}>Editar usuário</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{usuario.email}</Text>
        </Space>
      }
      okText="Salvar" cancelText="Cancelar">
      <Form layout="vertical" size="small">
        <Form.Item label="Nome completo">
          <Input value={nome} onChange={e => setNome(e.target.value)} />
        </Form.Item>

        <Collapse size="small" defaultActiveKey={['c', 'crea']}
          style={{ marginBottom: 14 }}
          items={[{
            key: 'c', label: <Text strong style={{ fontSize: 12 }}>🛍 Perfil pessoal (cargo, foto, contato)</Text>,
            children: (
              <>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 8 }}>
                  Foto e dados de contato. Aparecem em propostas (se for vendedor), assinaturas e perfil.
                </Text>
                <Row gutter={10}>
                  <Col span={12}><Form.Item label="Cargo"><Input value={cargo} onChange={e => setCargo(e.target.value)} placeholder="Ex.: Engenheiro Hídrico Sr." /></Form.Item></Col>
                  <Col span={12}><Form.Item label="Telefone comercial"><Input value={telComercial} onChange={e => setTelComercial(e.target.value)} placeholder="(51) 99999-9999" /></Form.Item></Col>
                  <Col span={12}><Form.Item label="LinkedIn"><Input value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="linkedin.com/in/usuario" /></Form.Item></Col>
                  <Col span={24}><Form.Item label="Foto (avatar, máx 4 MB)"><FotoUploader fotoAtual={fotoUrl} onChange={setFotoUrl} /></Form.Item></Col>
                </Row>
              </>
            ),
          }, {
            key: 'crea', label: <Text strong style={{ fontSize: 12 }}>🪪 Cadastro técnico (CREA — emissão de ART)</Text>,
            children: (
              <>
                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 8 }}>
                  Lidos quando este usuário é selecionado como RT em ART em Operacional &gt; Contratos.
                </Text>
                <Row gutter={10}>
                  <Col span={8}><Form.Item label="Carteira CREA"><Input value={crea} onChange={e => setCrea(e.target.value)} placeholder="Ex.: RS214787" /></Form.Item></Col>
                  <Col span={8}><Form.Item label="RNP"><Input value={creaRnp} onChange={e => setCreaRnp(e.target.value)} placeholder="Ex.: 2215003898" /></Form.Item></Col>
                  <Col span={8}><Form.Item label="Título profissional"><Input value={creaTitulo} onChange={e => setCreaTitulo(e.target.value)} placeholder="Ex.: Engenheiro Civil" /></Form.Item></Col>
                </Row>
              </>
            ),
          }]} />

        <Form.Item label="Perfis de acesso">
          {eEuMesmo && (
            <Alert type="warning" showIcon style={{ marginBottom: 10 }}
              message={<Text style={{ fontSize: 11 }}>O perfil <strong>admin</strong> não pode ser removido por você (anti-lockout).</Text>} />
          )}
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {grupos.map(g => {
              const bloqueado = eEuMesmo && g.name === 'admin' && selecionados.includes('admin');
              const checked = selecionados.includes(g.name);
              return (
                <Card key={g.name} size="small" hoverable={!bloqueado}
                  onClick={() => !bloqueado && toggle(g.name)}
                  style={{ background: checked ? '#E7ECFD' : '#fff',
                    borderColor: checked ? '#0930D0' : '#e0e0e0',
                  }}>
                  <Flex align="center" gap={10}>
                    <Checkbox checked={checked} onChange={() => {}} disabled={bloqueado} />
                    <Text strong style={{ color: checked ? '#0930D0' : '#333' }}>{g.name}</Text>
                    {g.description && <Text type="secondary" style={{ fontSize: 11 }}>— {g.description}</Text>}
                    {bloqueado && <Text style={{ marginLeft: 'auto', fontSize: 10, color: '#B07500' }}>🔒</Text>}
                  </Flex>
                </Card>
              );
            })}
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
